import React, { useState, useEffect, useRef, useCallback } from 'react';
import { slideApiService } from '../services/slideApiService';

interface CategoryEditorProps {
  categories: string[];
  onCategoriesChange: (categories: string[]) => void;
  shopId: string;
  className?: string;
  compact?: boolean;
  placeholder?: string;
}

const CategoryEditor: React.FC<CategoryEditorProps> = ({
  categories,
  onCategoriesChange,
  shopId,
  className = '',
  compact = false,
  placeholder = 'Add new category...'
}) => {
  const [newCategory, setNewCategory] = useState('');
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [showCategoryPopover, setShowCategoryPopover] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Debounced autocomplete search using Redis-backed endpoint
  const searchCategories = useCallback(async (prefix: string) => {
    if (!prefix.trim()) {
      setFilteredCategories([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await slideApiService.getCategoryAutocomplete(shopId, prefix.trim().toUpperCase());
      setFilteredCategories(results);
      setShowDropdown(results.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Failed to fetch category autocomplete:', error);
      setFilteredCategories([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, [shopId]);

  // Debounce the autocomplete search (300ms delay)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (newCategory.trim()) {
      debounceTimerRef.current = setTimeout(() => {
        searchCategories(newCategory);
      }, 300);
    } else {
      setFilteredCategories([]);
      setShowDropdown(false);
      setSelectedIndex(-1);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [newCategory, searchCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close category popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowCategoryPopover(false);
      }
    };

    if (showCategoryPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCategoryPopover]);

  const handleRemoveCategory = (categoryToRemove: string) => {
    const updatedCategories = categories.filter(cat => cat !== categoryToRemove);
    onCategoriesChange(updatedCategories);
  };

  const handleAddCategory = (categoryToAdd?: string) => {
    const category = categoryToAdd || newCategory;
    if (category.trim() && !categories.includes(category.trim().toUpperCase())) {
      const updatedCategories = [...categories, category.trim().toUpperCase()];
      onCategoriesChange(updatedCategories);
      setNewCategory('');
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  const handleToggleCategory = (category: string) => {
    if (categories.includes(category)) {
      onCategoriesChange(categories.filter(cat => cat !== category));
    } else {
      onCategoriesChange([...categories, category]);
    }
    inputRef.current?.focus();
  };

  const handleSelectAll = () => {
    const newCategories = filteredCategories.filter(cat => !categories.includes(cat));
    if (newCategories.length > 0) {
      onCategoriesChange([...categories, ...newCategories]);
    }
    setNewCategory('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && selectedIndex >= 0 && selectedIndex < filteredCategories.length) {
        // Toggle from dropdown
        handleToggleCategory(filteredCategories[selectedIndex]);
      } else if (!showDropdown) {
        // Add new custom category (only when dropdown is closed)
        handleAddCategory();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown) {
        setSelectedIndex(prev =>
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown) {
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCategory(e.target.value);
  };

  return (
    <div className={className}>
      {!compact && <h3 style={{ marginBottom: '16px' }}>Categories</h3>}

      {/* Full mode: categories pills above input */}
      {!compact && categories.length > 0 ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {categories.map((category, index) => (
            <div
              key={index}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 12px',
                backgroundColor: 'var(--bg-accent)',
                color: 'var(--primary-dark)',
                fontSize: '14px',
                borderRadius: '16px',
                border: '1px solid var(--border-medium)'
              }}
            >
              <span>{category}</span>
              <button
                type="button"
                onClick={() => handleRemoveCategory(category)}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary-dark)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1
                }}
                aria-label={`Remove ${category} category`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : !compact ? (
        <div style={{
          color: 'var(--text-secondary)',
          fontSize: '14px',
          fontStyle: 'italic',
          marginBottom: '16px'
        }}>
          No categories assigned
        </div>
      ) : null}

      {/* Add New Category */}
      <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
          <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={newCategory}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              style={{
                width: '100%',
                padding: '8px 32px 8px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            {newCategory && (
              <button
                type="button"
                onClick={() => {
                  setNewCategory('');
                  inputRef.current?.focus();
                }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1
                }}
                aria-label="Clear category input"
              >
                ×
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => handleAddCategory()}
            disabled={!newCategory.trim() || categories.includes(newCategory.trim().toUpperCase())}
            style={{
              padding: '8px 16px',
              backgroundColor: (!newCategory.trim() || categories.includes(newCategory.trim().toUpperCase())) ? 'var(--disabled-bg)' : 'var(--primary)',
              color: (!newCategory.trim() || categories.includes(newCategory.trim().toUpperCase())) ? 'var(--disabled)' : 'var(--text-inverse)',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '4px',
              border: 'none',
              cursor: (!newCategory.trim() || categories.includes(newCategory.trim().toUpperCase())) ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Add
          </button>
        </div>

        {/* Autocomplete Dropdown */}
        {showDropdown && filteredCategories.length > 0 && (() => {
          const uncheckedCount = filteredCategories.filter(cat => !categories.includes(cat)).length;
          return (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                borderRadius: '4px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000
              }}
            >
              {/* Select All row */}
              {uncheckedCount > 0 && (
                <div
                  onClick={handleSelectAll}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: 'var(--bg-primary)',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    borderBottom: '2px solid var(--border-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '15px' }}>Select All ({uncheckedCount})</span>
                </div>
              )}
              {filteredCategories.map((category, index) => {
                const isChecked = categories.includes(category);
                return (
                  <div
                    key={category}
                    onClick={() => handleToggleCategory(category)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedIndex === index ? 'var(--bg-accent)' : isChecked ? 'var(--warning-bg)' : 'var(--bg-card)',
                      color: selectedIndex === index ? 'var(--primary-dark)' : isChecked ? 'var(--text-accent)' : 'var(--text-primary)',
                      fontSize: '14px',
                      borderBottom: index < filteredCategories.length - 1 ? '1px solid var(--border-light)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{
                      fontSize: '16px',
                      lineHeight: 1,
                      color: isChecked ? 'var(--primary-dark)' : 'var(--text-secondary)',
                      flexShrink: 0
                    }}>
                      {isChecked ? '\u2611' : '\u2610'}
                    </span>
                    {category}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Loading indicator */}
        {isLoading && newCategory.trim() && (
          <div style={{
            color: 'var(--text-primary)',
            fontSize: '14px',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            Searching...
          </div>
        )}

        {/* Messages */}
        {newCategory.trim() && categories.includes(newCategory.trim().toUpperCase()) && (
          <div style={{
            color: 'var(--error-text)',
            fontSize: '14px',
            marginTop: '8px'
          }}>
            Category already exists
          </div>
        )}
        {newCategory.trim() && !isLoading && !showDropdown && !categories.includes(newCategory.trim().toUpperCase()) && (
          <div style={{
            color: 'var(--text-primary)',
            fontSize: '14px',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            No matching categories. Press Enter or click Add to create "{newCategory.trim().toUpperCase()}"
          </div>
        )}
      </div>

      {/* Compact mode: summary chip below input */}
      {compact && categories.length > 0 && (
        <div style={{ position: 'relative', marginTop: '8px' }} ref={popoverRef}>
          <div
            onClick={() => setShowCategoryPopover(!showCategoryPopover)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              backgroundColor: 'var(--bg-accent)',
              color: 'var(--text-accent)',
              fontSize: '14px',
              borderRadius: '16px',
              border: '1px solid var(--border-light)',
              cursor: 'pointer'
            }}
          >
            <span>Categories ({categories.length})</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCategoriesChange([]);
                setShowCategoryPopover(false);
              }}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--text-accent)',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1
              }}
              title="Clear all categories"
            >
              ×
            </button>
          </div>

          {showCategoryPopover && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-light)',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1001,
              minWidth: '200px',
              maxWidth: '350px'
            }}>
              {categories.map((category) => (
                <div
                  key={category}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    fontSize: '0.8125rem',
                    borderBottom: '1px solid var(--border-light)'
                  }}
                >
                  <span>{category}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCategory(category)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      padding: '0 4px',
                      lineHeight: 1,
                      flexShrink: 0
                    }}
                    aria-label={`Remove ${category}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryEditor;
